import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'

interface FullTextWithBgImageProps {
  image: string
  imageAlt?: string
  eyebrow?: string
  titleLine1: string
  titleLine2?: string
  titleLine3?: string
  body?: string
  signature?: string
  ctaLabel?: string
  ctaHref?: string
  height?: 'tall' | 'medium' | 'cinematic'
  align?: 'center' | 'left'
}

export function FullTextWithBgImage({
  image,
  imageAlt = '',
  eyebrow,
  titleLine1,
  titleLine2,
  titleLine3,
  body,
  signature,
  ctaLabel,
  ctaHref,
  height = 'tall',
  align = 'center',
}: FullTextWithBgImageProps) {
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })
  const yImage = useTransform(scrollYProgress, [0, 1], ['0%', '15%'])
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 1.2])
  const opacityFrame = useTransform(scrollYProgress, [0.1, 0.4], [0, 1])

  const heightClass =
    height === 'cinematic'
      ? 'h-[100svh] min-h-[640px]'
      : height === 'medium'
        ? 'h-[80svh] min-h-[560px]'
        : 'h-[90svh] min-h-[600px]'

  const alignContainer =
    align === 'center'
      ? 'items-center justify-center text-center'
      : 'items-end pb-32 md:pb-44'

  return (
    <section
      ref={ref}
      className={`relative ${heightClass} w-full overflow-hidden bg-bruno`}
      aria-label={titleLine1}
    >
      {/* Image with parallax + ken-burns */}
      <motion.div
        style={{ y: yImage, scale }}
        aria-hidden="true"
        className="absolute inset-0 -bottom-[15%]"
      >
        <img
          src={image}
          alt={imageAlt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </motion.div>

      {/* Tonal overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(20,15,12,0.35) 0%, rgba(20,15,12,0.15) 35%, rgba(20,15,12,0.65) 100%)',
        }}
      />
      <div className="nashi-grain" />

      {/* Editorial frame */}
      <motion.div
        style={{ opacity: opacityFrame }}
        className="pointer-events-none absolute inset-6 md:inset-12 border border-cream/15"
      />

      {/* Top decoration */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1.4, delay: 0.6 }}
        className="absolute top-12 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-3"
      >
        <span className="font-display italic text-cream/70 text-2xl">∞</span>
        <span className="block w-px h-12 bg-cream/30" />
      </motion.div>

      {/* Content */}
      <div
        className={`relative z-10 h-full flex ${alignContainer} px-6 md:px-12`}
      >
        <div
          className={`max-w-3xl ${align === 'center' ? 'mx-auto' : 'nashi-container w-full'}`}
        >
          {eyebrow && (
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.1 }}
              className={`inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.32em] text-cream/85 ${
                align === 'center' ? '' : ''
              }`}
            >
              <span className="block w-8 h-px bg-argan" />
              {eyebrow}
            </motion.span>
          )}

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.25, 1, 0.5, 1] }}
            className="mt-6 font-display text-cream"
            style={{
              fontSize: 'clamp(2.75rem, 7vw, 6rem)',
              lineHeight: 0.98,
              letterSpacing: '-0.02em',
            }}
          >
            <span className="block">{titleLine1}</span>
            {titleLine2 && (
              <span className="block italic font-light text-argan-soft">
                {titleLine2}
              </span>
            )}
            {titleLine3 && <span className="block">{titleLine3}</span>}
          </motion.h2>

          {body && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.45, ease: [0.25, 1, 0.5, 1] }}
              className={`mt-8 text-cream/85 text-base md:text-lg leading-relaxed ${
                align === 'center' ? 'max-w-xl mx-auto' : 'max-w-md'
              }`}
            >
              {body}
            </motion.p>
          )}

          {signature && (
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 1.4, delay: 0.7 }}
              className="mt-10 font-display italic text-argan-soft text-xl md:text-2xl"
            >
              — {signature}
            </motion.p>
          )}

          {ctaLabel && ctaHref && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.6, ease: [0.25, 1, 0.5, 1] }}
              className={`mt-10 ${align === 'center' ? 'flex justify-center' : ''}`}
            >
              <a
                href={ctaHref}
                className="group inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.24em] text-cream pb-2 border-b border-cream/60 hover:border-argan transition-colors"
              >
                {ctaLabel}
                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5">
                  →
                </span>
              </a>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  )
}
